<template>
  <el-select
    v-model="selectedValue"
    :placeholder="placeholder"
    :clearable="clearable"
    :filterable="filterable"
    :disabled="disabled"
    :multiple="multiple"
    @clear="handleClear"
    @remove-tag="handleRemoveTag"
    ref="selectRef"
  >
    <el-option :value="optionValue" :label="optionLabel" style="display: none" />
    <el-tree
      ref="treeRef"
      :data="treeData"
      :props="treeProps"
      :node-key="nodeKey"
      :default-expand-all="defaultExpandAll"
      :expand-on-click-node="false"
      :check-on-click-node="multiple"
      :highlight-current="!multiple"
      :show-checkbox="multiple"
      :check-strictly="checkStrictly"
      :filter-node-method="filterNodeMethod"
      @node-click="handleNodeClick"
      @check="handleCheck"
    />
  </el-select>
</template>

<script setup>
import { ref, watch, computed, nextTick } from 'vue'

const props = defineProps({
  // 树形数据
  treeData: {
    type: Array,
    default: () => []
  },
  // 树形配置项
  treeProps: {
    type: Object,
    default: () => ({
      children: 'children',
      label: 'label',
      disabled: 'disabled'
    })
  },
  // 选中的值
  modelValue: {
    type: [String, Number, Array],
    default: () => ([])
  },
  // 节点唯一标识
  nodeKey: {
    type: String,
    default: 'id'
  },
  // 占位符
  placeholder: {
    type: String,
    default: '请选择'
  },
  // 是否可清空
  clearable: {
    type: Boolean,
    default: true
  },
  // 是否可搜索
  filterable: {
    type: Boolean,
    default: true
  },
  // 是否禁用
  disabled: {
    type: Boolean,
    default: false
  },
  // 是否多选
  multiple: {
    type: Boolean,
    default: false
  },
  // 是否默认展开所有节点
  defaultExpandAll: {
    type: Boolean,
    default: false
  },
  // 是否严格遵循父子不互相关联
  checkStrictly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const selectRef = ref(null)
const treeRef = ref(null)
const selectedValue = ref(props.multiple ? [] : '')

// 计算显示的标签文本
const optionLabel = computed(() => {
  if (props.multiple) {
    return selectedValue.value.map(id => {
      const node = treeRef.value?.getNode(id)
      return node ? node.data[props.treeProps.label] : ''
    }).join(', ')
  } else {
    const node = treeRef.value?.getNode(selectedValue.value)
    return node ? node.data[props.treeProps.label] : ''
  }
})

// 计算选项值（用于隐藏的el-option）
const optionValue = computed(() => {
  return props.multiple ? selectedValue.value.join(',') : selectedValue.value
})

// 初始化选中状态
const initSelected = () => {
  if (!props.treeData.length) return
  
  if (props.multiple) {
    selectedValue.value = Array.isArray(props.modelValue) ? [...props.modelValue] : []
    nextTick(() => {
      treeRef.value?.setCheckedKeys(selectedValue.value)
    })
  } else {
    selectedValue.value = props.modelValue
    nextTick(() => {
      if (selectedValue.value) {
        treeRef.value?.setCurrentKey(selectedValue.value)
      }
    })
  }
}

// 节点点击事件
const handleNodeClick = (data, node) => {
  if (props.multiple) return
  
  selectedValue.value = node.key
  emit('update:modelValue', selectedValue.value)
  emit('change', selectedValue.value, node)
  selectRef.value?.blur()
}

// 节点勾选事件
const handleCheck = (data, { checkedKeys }) => {
  if (!props.multiple) return
  
  selectedValue.value = checkedKeys
  emit('update:modelValue', selectedValue.value)
  emit('change', selectedValue.value, treeRef.value?.getCheckedNodes())
}

// 清空选择
const handleClear = () => {
  if (props.multiple) {
    selectedValue.value = []
    treeRef.value?.setCheckedKeys([])
  } else {
    selectedValue.value = ''
    treeRef.value?.setCurrentKey(null)
  }
  emit('update:modelValue', selectedValue.value)
  emit('change', selectedValue.value, null)
}

// 移除标签（多选时）
const handleRemoveTag = (tag) => {
  if (!props.multiple) return
  
  const index = selectedValue.value.indexOf(tag)
  if (index > -1) {
    selectedValue.value.splice(index, 1)
    treeRef.value?.setCheckedKeys(selectedValue.value)
    emit('update:modelValue', selectedValue.value)
    emit('change', selectedValue.value, treeRef.value?.getCheckedNodes())
  }
}

// 过滤节点方法
const filterNodeMethod = (value, data, node) => {
  if (!value) return true
  return node.label?.includes(value)
}

// 暴露方法
defineExpose({
  filter: (value) => {
    treeRef.value?.filter(value)
  }
})

// 监听modelValue变化
watch(() => props.modelValue, (val) => {
  if (props.multiple) {
    if (JSON.stringify(val) !== JSON.stringify(selectedValue.value)) {
      selectedValue.value = Array.isArray(val) ? [...val] : []
      treeRef.value?.setCheckedKeys(selectedValue.value)
    }
  } else {
    if (val !== selectedValue.value) {
      selectedValue.value = val
      treeRef.value?.setCurrentKey(selectedValue.value)
    }
  }
})

// 监听treeData变化
watch(() => props.treeData, () => {
  initSelected()
}, { deep: true })

// 初始化
initSelected()
</script>

<style scoped>
.el-select-dropdown__item {
  padding: 0;
  height: auto;
}

.el-tree {
  padding: 5px;
}

:deep(.el-select-dropdown__list) {
  padding: 0 !important;
}
</style>